import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Modal,
  Alert,
  Card,
  CardContent,
  alpha,
  useTheme,
  // Link,
  IconButton,
  Collapse,
  Checkbox,
  ListItemText,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Search as SearchIcon,
  ClearAll,
  CalendarToday,
  Warning,
  KeyboardArrowDown,
  KeyboardArrowRight
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import ChildBidDetails from './ChildBidDetails';
import { addCommaWithTwoDecimals } from 'src/services/common';

interface AwardedToContact {
  name: string;
  email: string;
  phone: string;
}

// Types
interface Bid {
  bidId: string;
  bidType: string;
  title: string;
  category: string;
  adDate: string;
  openingDate: string;
  status: string;
  awardedTo?: string;
  uploadedFiles: number;
  departmentId: string;
  ContractNo: string;
  description?: string;
  documents?: {
    id: number;
    name: string;
    type: string;
    description?: string;
    documentUrl: string;
  }[];
  awardedToVendor?: any;
  awardedToContact?: AwardedToContact | null;
  awardAmount?: string;
  isAddendum: boolean;
  childItems?: Bid[];
}

interface SearchFilters {
  title: string;
  contractNo: string;
  awardedTo: string;

  categories: number[];
  statuses: number[];
  bidTypes: number[];
  adDate: Date | null;
  openingDate: Date | null;
}

// Dropdown data interfaces
interface TypeOption {
  TypeID: number;
  Type: string;
  Active: boolean;
}

interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

interface StatusOption {
  StatusID: number;
  Status: string;
  Active: boolean;
}

interface BidResponse {
  AwardedToVendor: any;
  BidID: number;
  TypeID: number;
  ContractNo: string;
  Title: string;
  CategoryIDs: number[];
  DepartmentID: number;
  GoLiveDate: string;
  BidDeadLineDate: string;
  AdDate: string;
  OpeningDate: string;
  AwardDate: string | null;
  StatusID: number;
  Description: string | null;
  AdminNote: string | null;
  IsPublish: boolean;
  ReasonToReopen: string | null;
  AwardedTo: string | null;
  ReasonToReject: string | null;
  TownshipID: number;
  Active: boolean;
  RowStatus: number;
  CreateDate: string;
  CreatedBy: number;
  UpdateDate: string;
  UpdatedBy: number;
  DeleteDate: string | null;
  DeletedBy: number | null;
  Categories: {
    CategoryID: number;
    CategoryName: string;
  }[];
  Status: string;
  Type: string;
  Documents: {
    BidDocsID: number;
    DocumentName: string;
    DocumentType: string;
    DocumentDescription: string;
    DocumentUrl: string;
  }[];
  AwardedToContact?: {
    Name: string;
    Email: string;
    Phone: string;
  };
  AwardAmount?: number;
  IsAddendum: boolean;
  ChildBidDocuments: {
    ChildBidDocsID: number;
    DocumentName: string;
    DocumentType: string;
    DocumentDescription: string;
    DocumentUrl: string;
    InsertDate: string;
  }[];
}

interface APIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    TotalPages: number;
    Data: BidResponse[];
  };
}

const Home: React.FC = () => {
  const { userDetails } = useSelector((state: RootState) => state.user);

  const theme = useTheme();
  const navigate = useNavigate();

  // State management
  const [filters, setFilters] = useState<SearchFilters>({
    title: '',
    contractNo: '',
    awardedTo: '',

    categories: [],
    statuses: [],
    bidTypes: [],
    adDate: null,
    openingDate: null
  });

  const { state: locationState, township, uuid } = useParams();
  const [bids, setBids] = useState<Bid[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showTerms, setShowTerms] = useState(false);

  // State for API data
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdown data
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [openCategorySelect, setOpenCategorySelect] = useState(false);
  const [openStatusSelect, setOpenStatusSelect] = useState(false);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  // Fixed: Add missing state variables

  const [loadingChildItems, setLoadingChildItems] = useState<{
    [key: string]: boolean;
  }>({});

  const [selectedChildBidId, setSelectedChildBidId] = useState(null);
  const [viewChildBidDetails, setViewChildBidDetails] = useState(false);

  // Fetch dropdown data (categories, types, statuses)
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      setDropdownError(null);
      try {
        // Fetch categories
        const categoriesResponse = await httpRequest(
          'GET',
          `/bid_portal/category/get_all`,
          {},
          {}
        );

        if (categoriesResponse.status === 'success') {
          const categoriesData =
            categoriesResponse.data?.data ||
            categoriesResponse.data?.Data ||
            [];
          setCategories(categoriesData);
        } else {
          console.error('Failed to fetch categories:', categoriesResponse);
          setDropdownError('Failed to load categories. Please try again.');
        }

        // Fetch types
        const typesResponse = await httpRequest(
          'GET',
          `/bid_portal/type/get_all`,
          {},
          {}
        );

        if (typesResponse.status === 'success') {
          const typesData =
            typesResponse.data?.data || typesResponse.data?.Data || [];
          setTypes(typesData);
        } else {
          console.error('Failed to fetch types:', typesResponse);
          setDropdownError('Failed to load bid types. Please try again.');
        }

        // Fetch statuses
        const statusesResponse = await httpRequest(
          'GET',
          `/bid_portal/status/get_all`,
          {},
          {}
        );

        if (statusesResponse.status === 'success') {
          const statusesData =
            statusesResponse.data?.data || statusesResponse.data?.Data || [];
          setStatuses(statusesData);
        } else {
          console.error('Failed to fetch statuses:', statusesResponse);
          setDropdownError('Failed to load statuses. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setDropdownError('Failed to load form options. Please try again.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  // const getBidResultDocument = (documents: any) => {
  //   return documents.find((doc) => doc.description === 'Bid Result Document');
  // };

  const fetchBidsWithFilters = async (
    pageNum: number,
    pageSize: number,
    searchFilters: SearchFilters = filters
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1)?.toString(),
        Size: pageSize?.toString(),
        ...(searchFilters.title && { Title: searchFilters.title }),
        ...(searchFilters.categories?.length > 0 && {
          Categories: searchFilters.categories.join(',')
        }),
        ...(searchFilters.statuses?.length > 0 && {
          Status: searchFilters.statuses.join(',')
        }),
        ...(searchFilters.bidTypes?.length > 0 && {
          Type: searchFilters.bidTypes.join(',')
        }),
        ...(searchFilters.adDate && {
          AdDate: searchFilters.adDate.toISOString()
        }),
        ...(searchFilters.openingDate && {
          OpeningDate: searchFilters.openingDate.toISOString()
        }),
        ...(searchFilters.contractNo && {
          ContractNo: searchFilters.contractNo
        }),
        ...(searchFilters.awardedTo && { AwardedTo: searchFilters.awardedTo })
      });

      const response = (await httpRequest(
        'GET',
        `/bid_portal/bid_details/get?${queryParams.toString()}`
      )) as APIResponse;

      if (response.status === 'success') {
        const transformedBids: any[] = response?.data?.Data?.map((bid) => ({
          bidId: bid?.BidID.toString(),
          bidType: bid?.Type,
          title: bid?.Title,
          category: bid?.Categories?.map((cat) => cat?.CategoryName)?.join(
            ', '
          ),
          adDate: bid?.AdDate,
          openingDate: bid?.OpeningDate,
          status: bid?.Status,
          awardedTo: bid?.AwardedTo || '',
          uploadedFiles: bid?.Documents?.length,
          departmentId: bid?.DepartmentID?.toString(),
          ContractNo: bid?.ContractNo,
          description: bid?.Description || '',
          awardedToVendor: bid?.AwardedToVendor?.Business || null,
          awardAmount: bid?.AwardAmount,
          awardedToContact: bid?.AwardedToContact
            ? {
                name: bid?.AwardedToContact.Name,
                email: bid?.AwardedToContact.Email,
                phone: bid?.AwardedToContact.Phone
              }
            : null,
          documents: bid?.Documents?.map((doc) => ({
            id: doc.BidDocsID,
            name: doc.DocumentName,
            type: doc.DocumentType,
            description: doc.DocumentDescription,
            documentUrl: doc.DocumentUrl
          })),
          isAddendum: bid?.IsAddendum,
          childItems: [],
          childBidDocuments: bid?.ChildBidDocuments
        }));

        setBids(transformedBids);
        setTotalCount(response.data.TotalRecords);
      } else {
        toast.error(response.message || 'Failed to fetch bids');
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
      toast.error('Failed to fetch bids. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async (pageNum: number, pageSize: number) => {
    await fetchBidsWithFilters(pageNum, pageSize);
  };

  useEffect(() => {
    fetchBids(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchBidsWithFilters(0, rowsPerPage);
    setExpandedRows({});
  };
  const handleClear = () => {
    // Create the cleared filters object
    const clearedFilters = {
      title: '',
      categories: [],
      statuses: [],
      bidTypes: [],
      adDate: null,
      openingDate: null,
      contractNo: '',
      awardedTo: ''
    };

    // Update the state
    setFilters(clearedFilters);
    setPage(0);

    // Call fetchBids with the cleared filters directly
    fetchBidsWithFilters(0, rowsPerPage, clearedFilters);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShowDetails = (BidID: string) => {
    navigate(
      `/${locationState}/${township}/${uuid}/bid-portal/home/bid-details/${BidID}`
    );
  };

  const handleShowChildBidDetails = (childBid) => {
    setSelectedChildBidId(childBid);
    setViewChildBidDetails(true);
    window.scrollTo(0, 0);
  };

  const handleBackToBidList = () => {
    setViewChildBidDetails(false);
    setSelectedChildBidId(null);
  };

  // Function to format dates nicely
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Get status color based on status text
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'open':
        return theme.palette.success.main;
      case 'closed':
      case 'close':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'awarded':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  if (viewChildBidDetails && Object.entries(selectedChildBidId)?.length > 0) {
    return (
      <ChildBidDetails
        bidData={selectedChildBidId}
        onBack={handleBackToBidList}
      />
    );
  }

  const isDetailDisabled = (bid: Bid) => {
    if (userDetails?.id !== bid?.awardedTo && bid.status !== 'Open') {
      return true;
    }
    return false;
  };

  // const isPODisabled = (bid: Bid) => {
  //   if (userDetails?.id !== bid?.awardedTo) {
  //     return true;
  //   }
  //   return false;
  // };

  // // Update your function to navigate to the UploadPOFIle page
  // const handleUploadPoDdetails = (bid: Bid) => {
  //   navigate(
  //     `/${locationState}/${township}/${uuid}/bid-portal/home/po-details/${bid.bidId}`
  //   );
  // };

  const fetchChildItems = async (bidId: string) => {
    setLoadingChildItems((prev) => ({ ...prev, [bidId]: true }));

    try {
      const response = await httpRequest(
        'GET',
        `/bid_portal/childBid?BidID=${bidId}`,
        {},
        {}
      );

      if (response.status === 'success') {
        const childItems =
          response?.data?.data?.map((child: any) => ({
            ...child, // Keep all original properties from API
            bidId: child.ChildBidID?.toString() || child.bidId?.toString(), // Ensure bidId is set
            contractNo: child.ChildBidContractNo || child.contractNo
            // Add any other mappings you need
          })) || [];

        const updatedBids = bids?.map((bidItem) => {
          return bidItem.bidId === bidId
            ? { ...bidItem, childItems: childItems }
            : bidItem;
        });

        setBids(updatedBids);
      } else {
        console.error('Failed to fetch child items:', response);
        toast.error(response.message || 'Failed to load child items');
      }
    } catch (error) {
      console.error('Error fetching child items:', error);
      toast.error('Failed to fetch child items. Please try again later.');
    } finally {
      setLoadingChildItems((prev) => ({ ...prev, [bidId]: false }));
    }
  };

  const toggleRowExpansion = (bidId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [bidId]: !prev[bidId]
    }));

    // Only fetch child items if the row is being expanded (not collapsed)
    // and if child items haven't been loaded yet
    if (!expandedRows[bidId]) {
      const currentBid = bids.find((bid) => bid.bidId === bidId);
      if (
        currentBid?.isAddendum &&
        (!currentBid.childItems || currentBid.childItems?.length === 0)
      ) {
        fetchChildItems(bidId);
      }
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 50,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Home
        </Typography>
      </Box>

      {dropdownError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          {dropdownError}
        </Alert>
      )}

      {/* <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography color="text.secondary">
          <strong>Note:</strong> For bids crossed bid deadline and not awarded,
          search with Close status.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIsSearchExpanded(!isSearchExpanded)}
          sx={{
            borderRadius: 1.5,
            fontWeight: 'medium'
          }}
        >
          {isSearchExpanded ? 'Minimize' : 'Expand'}
        </Button>
      </Box> */}

      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Filters
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {loadingDropdowns ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {/* First row - 3 fields */}
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Title"
                  variant="outlined"
                  value={filters.title}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      title: e.target.value
                    })
                  }
                  InputProps={{
                    sx: {
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)'
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Awarded To"
                  variant="outlined"
                  value={filters.awardedTo}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      awardedTo: e.target.value
                    })
                  }
                  InputProps={{
                    sx: {
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)'
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Contract No"
                  variant="outlined"
                  value={filters.contractNo}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      contractNo: e.target.value
                    })
                  }
                  InputProps={{
                    sx: {
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)'
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    open={openCategorySelect}
                    onOpen={() => setOpenCategorySelect(true)}
                    onClose={() => setOpenCategorySelect(false)}
                    multiple
                    value={filters.categories}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allCategoryIDs = categories?.map(
                          (cat) => cat.CategoryID
                        );
                        const isAllSelected =
                          filters.categories?.length === categories?.length;

                        // If all categories are selected, deselect all; otherwise, select all
                        const finalCategories = isAllSelected
                          ? []
                          : allCategoryIDs;

                        setFilters({
                          ...filters,
                          categories: finalCategories
                        });
                      } else {
                        // Ensure no invalid "Unknown" category is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            categories.some((cat) => cat.CategoryID === value)
                        );

                        setFilters({
                          ...filters,
                          categories: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select categories
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((CategoryID) => {
                              const categoryName =
                                categories.find(
                                  (c) => c.CategoryID === CategoryID
                                )?.CategoryName || 'Unknown';
                              return (
                                <Chip
                                  key={CategoryID}
                                  label={categoryName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          filters.categories?.length === categories?.length
                        }
                        indeterminate={
                          filters.categories?.length > 0 &&
                          filters.categories?.length < categories?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Category Options */}
                    {categories?.map((category) => (
                      <MenuItem
                        key={category.CategoryID}
                        value={category.CategoryID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            filters.categories.indexOf(category.CategoryID) > -1
                          }
                        />
                        {category.CategoryName}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenCategorySelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Status
                  </InputLabel>
                  <Select
                    open={openStatusSelect}
                    onOpen={() => setOpenStatusSelect(true)}
                    onClose={() => setOpenStatusSelect(false)}
                    multiple
                    value={filters.statuses}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allStatusIDs = statuses?.map(
                          (status) => status.StatusID
                        );
                        const isAllSelected =
                          filters.statuses?.length === statuses?.length;

                        // If all statuses are selected, deselect all; otherwise, select all
                        const finalStatuses = isAllSelected ? [] : allStatusIDs;

                        setFilters({
                          ...filters,
                          statuses: finalStatuses
                        });
                      } else {
                        // Ensure no invalid "Unknown" status is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            statuses.some((status) => status.StatusID === value)
                        );

                        setFilters({
                          ...filters,
                          statuses: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select statuses
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((statusId) => {
                              const statusName =
                                statuses.find((s) => s.StatusID === statusId)
                                  ?.Status || 'Unknown';
                              return (
                                <Chip
                                  key={statusId}
                                  label={statusName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={filters.statuses?.length === statuses?.length}
                        indeterminate={
                          filters.statuses?.length > 0 &&
                          filters.statuses?.length < statuses?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Status Options */}
                    {statuses?.map((status) => (
                      <MenuItem
                        key={status.StatusID}
                        value={status.StatusID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            filters.statuses.indexOf(status.StatusID) > -1
                          }
                        />
                        {status.Status}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenStatusSelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Type
                  </InputLabel>
                  <Select
                    open={openTypeSelect}
                    onOpen={() => setOpenTypeSelect(true)}
                    onClose={() => setOpenTypeSelect(false)}
                    multiple
                    value={filters.bidTypes}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allTypeIDs = types?.map((type) => type.TypeID);
                        const isAllSelected =
                          filters.bidTypes?.length === types?.length;

                        // If all types are selected, deselect all; otherwise, select all
                        const finalTypes = isAllSelected ? [] : allTypeIDs;

                        setFilters({
                          ...filters,
                          bidTypes: finalTypes
                        });
                      } else {
                        // Ensure no invalid "Unknown" type is included
                        const validSelectedValues = selectedValues.filter(
                          (value) => types.some((type) => type.TypeID === value)
                        );

                        setFilters({
                          ...filters,
                          bidTypes: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select types
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((TypeID) => {
                              const TypeName =
                                types.find((t) => t.TypeID === TypeID)?.Type ||
                                'Unknown';
                              return (
                                <Chip
                                  key={TypeID}
                                  label={TypeName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={filters.bidTypes?.length === types?.length}
                        indeterminate={
                          filters.bidTypes?.length > 0 &&
                          filters.bidTypes?.length < types?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Type Options */}
                    {types?.map((type) => (
                      <MenuItem
                        key={type.TypeID}
                        value={type.TypeID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={filters.bidTypes.indexOf(type.TypeID) > -1}
                        />
                        {type.Type}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenTypeSelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Second row with date picker */}
              <Grid item xs={12} sm={4} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Ad Date"
                    value={filters.adDate}
                    onChange={(date) =>
                      setFilters({ ...filters, adDate: date })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'outlined',
                        InputProps: {
                          startAdornment: (
                            <CalendarToday
                              sx={{
                                color: theme.palette.text.secondary,
                                mr: 1,
                                fontSize: '1rem'
                              }}
                            />
                          ),
                          sx: {
                            borderRadius: 1.5,
                            height: 40,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.5
                              )
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2
                            }
                          }
                        },
                        sx: {
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            fontSize: '0.875rem',
                            transform: 'translate(14px, 11px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 500
                          }
                        }
                      },
                      desktopPaper: {
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          '& .MuiPickersDay-root.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Opening Date"
                    value={filters.openingDate}
                    onChange={(date) =>
                      setFilters({ ...filters, openingDate: date })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'outlined',
                        InputProps: {
                          startAdornment: (
                            <CalendarToday
                              sx={{
                                color: theme.palette.text.secondary,
                                mr: 1,
                                fontSize: '1rem'
                              }}
                            />
                          ),
                          sx: {
                            borderRadius: 1.5,
                            height: 40,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.5
                              )
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2
                            }
                          }
                        },
                        sx: {
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            fontSize: '0.875rem',
                            transform: 'translate(14px, 11px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 500
                          }
                        }
                      },
                      desktopPaper: {
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          '& .MuiPickersDay-root.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    height: '100%',
                    alignItems: 'center'
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    startIcon={<ClearAll />}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 'bold',
                      borderWidth: '1px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderWidth: '1px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Bids
          </Typography>
          {!loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Total Results: {totalCount}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    py: 1.8
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Contract No.</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Ad Date</TableCell>
                    <TableCell>Opening Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Awarded To</TableCell>
                    {/* <TableCell align="center">Bid Results</TableCell>
                    <TableCell align="center">PO</TableCell> */}
                    <TableCell align="center">Award Amount</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bids?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No bids found. Try adjusting your search criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bids?.map((bid) => (
                      <>
                        <TableRow
                          key={bid.bidId}
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.03)
                            },
                            transition: 'background-color 0.2s ease',
                            '&:nth-of-type(even)': {
                              bgcolor: alpha(
                                theme.palette.background.default,
                                0.4
                              )
                            }
                          }}
                        >
                          <TableCell>
                            {/* Check if this bid has isAddendum */}
                            {bid.isAddendum && (
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpansion(bid.bidId)}
                                sx={{
                                  transition: 'transform 0.2s ease',
                                  color: theme.palette.primary.main
                                }}
                              >
                                {expandedRows[bid.bidId] ? (
                                  <KeyboardArrowDown />
                                ) : (
                                  <KeyboardArrowRight />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {bid.ContractNo || '-'}
                          </TableCell>
                          <TableCell>
                            {bid.bidType ? (
                              <Chip
                                label={bid.bidType}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.main,
                                  fontWeight: 500,
                                  borderRadius: 1
                                }}
                              />
                            ) : null}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {bid.title}
                          </TableCell>
                          <TableCell>
                            {bid?.adDate
                              ? new Date(bid.adDate).toLocaleDateString('en-US')
                              : '-'}
                          </TableCell>
                          <TableCell>{formatDate(bid.openingDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={bid.status}
                              size="small"
                              sx={{
                                bgcolor: alpha(getStatusColor(bid.status), 0.1),
                                color: getStatusColor(bid.status),
                                fontWeight: 'bold',
                                borderRadius: 1
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                bid?.awardedToVendor ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {bid.awardedToVendor}
                                    </Typography>
                                    {bid.awardedToContact?.name && (
                                      <Typography variant="body2">Contact: {bid.awardedToContact.name}</Typography>
                                    )}
                                    {bid.awardedToContact?.email && (
                                      <Typography variant="body2">Email: {bid.awardedToContact.email}</Typography>
                                    )}
                                    {bid.awardedToContact?.phone && (
                                      <Typography variant="body2">Phone: {bid.awardedToContact.phone}</Typography>
                                    )}
                                  </Box>
                                ) : ''
                              }
                            >
                              <span>{bid?.awardedToVendor || '-'}</span>
                            </Tooltip>
                          </TableCell>
                          {/* <TableCell
                            sx={{
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'center'
                            }}
                          >
                            {getBidResultDocument(bid.documents) ? (
                              // View Button
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() =>
                                  window.open(
                                    getBidResultDocument(bid.documents)!
                                      .documentUrl,
                                    '_blank'
                                  )
                                }
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: theme.palette.primary.dark
                                  }
                                }}
                              >
                                View
                              </Link>
                            ) : (
                              '-'
                            )}
                          </TableCell> */}
                          {/* <TableCell align="center">
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleUploadPoDdetails(bid)}
                              sx={{
                                color: isPODisabled(bid)
                                  ? theme.palette.text.disabled
                                  : theme.palette.primary.main,
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                cursor: isPODisabled(bid)
                                  ? 'not-allowed'
                                  : 'pointer',
                                opacity: isPODisabled(bid) ? 0.6 : 1,
                                '&:hover': {
                                  textDecoration: isPODisabled(bid)
                                    ? 'none'
                                    : 'underline',
                                  color: isPODisabled(bid)
                                    ? theme.palette.text.disabled
                                    : theme.palette.primary.dark
                                }
                              }}
                              disabled={isPODisabled(bid)}
                            >
                              View
                            </Link>
                          </TableCell> */}

                          <TableCell align="center" sx={{ fontWeight: 500 }}>
                            {!isDetailDisabled(bid)
                              ? bid.awardAmount ? `$${addCommaWithTwoDecimals(bid.awardAmount)}` : '-'
                              : '-'}
                          </TableCell>

                          <TableCell align="center">
                            <Button
                              size="small"
                              color="primary"
                              variant="contained"
                              onClick={() => handleShowDetails(bid.bidId)}
                              sx={{
                                borderRadius: 1.5,
                                px: 2,
                                py: 0.5,
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                                  transform: 'translateY(-2px)'
                                }
                              }}
                              disabled={isDetailDisabled(bid)}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={16} // Updated to match parent table column count
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                          >
                            <Collapse
                              in={expandedRows[bid.bidId]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 2 }}>
                                {loadingChildItems[bid.bidId] ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      p: 3
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ ml: 2 }}
                                    >
                                      Loading child items...
                                    </Typography>
                                  </Box>
                                ) : bid.childItems &&
                                  bid.childItems?.length > 0 ? (
                                  <Table
                                    sx={{
                                      bgcolor: alpha(
                                        theme.palette.grey[100],
                                        0.5
                                      )
                                    }}
                                  >
                                    <TableHead>
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Contract No.
                                        </TableCell>

                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Title
                                        </TableCell>

                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Ad Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Opening Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Status
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 'bold',
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.08
                                            )
                                          }}
                                        >
                                          Actions
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {bid?.childItems?.map((childBid: any) => (
                                        <TableRow key={childBid.ChildBidID}>
                                          <TableCell sx={{ fontWeight: 500 }}>
                                            {childBid?.ChildBidContractNo}
                                          </TableCell>

                                          <TableCell sx={{ fontWeight: 500 }}>
                                            {childBid?.Title}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 500 }}>
                                            {childBid?.AdDate
                                              ? new Date(
                                                  childBid?.AdDate
                                                ).toLocaleDateString('en-US')
                                              : '-'}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 500 }}>
                                            {formatDate(childBid?.OpeningDate)}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 500 }}>
                                            {childBid?.Status}
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              size="small"
                                              color="primary"
                                              variant="contained"
                                              onClick={() =>
                                                handleShowChildBidDetails(
                                                  childBid
                                                )
                                              }
                                              sx={{
                                                borderRadius: 1.5,
                                                px: 2,
                                                py: 0.5,
                                                fontWeight: 'bold',
                                                boxShadow:
                                                  '0 2px 8px rgba(0, 0, 0, 0.15)',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                  boxShadow:
                                                    '0 4px 10px rgba(0, 0, 0, 0.18)',
                                                  transform: 'translateY(-2px)'
                                                }
                                              }}
                                              disabled={isDetailDisabled(bid)}
                                            >
                                              Details
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ p: 2, textAlign: 'center' }}
                                  >
                                    No child items found for this bid.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      fontWeight: 500
                    },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    '&:focus': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Terms and Conditions Modal */}
      <Modal
        open={showTerms}
        onClose={() => setShowTerms(false)}
        aria-labelledby="terms-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            maxHeight: '80vh',
            overflow: 'auto',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" id="terms-modal-title" sx={{ mb: 2 }}>
            Terms Of Download
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Login is required
          </Typography>
          <Typography paragraph>
            It is the responsibility of the Vendor to ensure all pages of the
            bid package have been downloaded and printed properly.
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={() =>
                navigate(`/${locationState}/${township}/${uuid}/login`)
              }
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.18)'
                }
              }}
            >
              Agree & Proceed
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowTerms(false)}
              sx={{
                borderRadius: 1.5,
                px: 3,
                py: 1,
                fontWeight: 'medium',
                borderWidth: '1.5px',
                '&:hover': {
                  borderWidth: '1.5px',
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Disagree
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Home;
